import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import type { QuotaResource } from '@/lib/permissions/usePlanQuota';
import { useT } from '@/lib/i18n/useT';

interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resource: QuotaResource;
  currentPlanName: string;
  limit: number;
}

const RESOURCE_KEY: Record<QuotaResource, string> = {
  projects: 'upgradeDialog.resource.projects',
  groups: 'upgradeDialog.resource.groups',
  events: 'upgradeDialog.resource.events',
  maxBrands: 'upgradeDialog.resource.brands',
  maxUsers: 'upgradeDialog.resource.users',
  maxRoles: 'upgradeDialog.resource.roles',
  maxSurveys: 'upgradeDialog.resource.surveys',
  xotskiEvents: 'upgradeDialog.resource.events',
  xotskiEventGroups: 'upgradeDialog.resource.groups',
  xotskiStations: 'upgradeDialog.resource.stations',
  xotskiStocks: 'upgradeDialog.resource.stocks',
};

export function UpgradeDialog({
  open,
  onOpenChange,
  resource,
  currentPlanName,
  limit,
}: UpgradeDialogProps) {
  const navigate = useNavigate();
  const t = useT();
  const resourceLabel = t(RESOURCE_KEY[resource]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="mb-4 grid h-12 w-12 place-items-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]">
            <Sparkles className="h-5 w-5" />
          </div>
          <DialogTitle>{t('upgradeDialog.title', { resource: resourceLabel })}</DialogTitle>
          <DialogDescription>
            {t('upgradeDialog.body', { plan: currentPlanName, limit, resource: resourceLabel })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('upgradeDialog.maybeLater')}
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false);
              navigate('/pricing');
            }}
          >
            {t('upgradeDialog.seePlans')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
